import React, { ComponentProps, createRef, memo, PropsWithChildren, useCallback, useEffect, useMemo } from 'react'
import { LayoutChangeEvent, View, ViewStyle, DimensionValue } from 'react-native'
import { create } from 'zustand'
import { PortalHost } from '@gorhom/portal'

import { isWeb } from '@/components/WebWrapper'

type LayoutRegistry = {
  name: string
  layout?: {
    x: number
    y: number
    width: number
    height: number
  }
  _eventTarget: LayoutChangeEvent['target']
}

type LayoutRegistryStore = {
  registryLayout: Record<LayoutRegistry['name'], LayoutRegistry>
  add: (registry: LayoutRegistry) => void
  updateLayout: (name: LayoutRegistry['name'], layout: LayoutRegistry['layout']) => void
  remove: (name: string) => void
}

const useLayoutShiftRegistryStore = create<LayoutRegistryStore>((set, get) => ({
  registryLayout: {},
  add: (registry: LayoutRegistry) => {
    set((s) => {
      s.registryLayout[registry.name] = registry
      return {
        registryLayout: s.registryLayout
      }
    })
  },
  updateLayout: (name, layout) => {
    set((s) => {
      const registry = s.registryLayout[name]
      if (!registry) {
        return s
      }
      s.registryLayout[name] = { ...registry, layout }
      return {
        registryLayout: s.registryLayout
      }
    })
  },
  remove: (name: string) => {
    set((s) => {
      delete s.registryLayout[name]
      return {
        registryLayout: s.registryLayout
      }
    })
  }
}))

export const useLayoutShiftRegistry = () => {
  const unregister = useCallback((name: string) => {
    useLayoutShiftRegistryStore.getState().remove(name)
  }, [])

  const register = useCallback((name: string, event: LayoutChangeEvent) => {
    if (isWeb) {
      // @ts-ignore this is for web
      const measure = event.nativeEvent?.target?.getBoundingClientRect() as DOMRect
      console.log('LOG: useLayoutShiftRegistry > register > measure:', name, measure)
      useLayoutShiftRegistryStore.getState().add({
        // @ts-ignore this is for web
        _eventTarget: event.nativeEvent?.target,
        name,
        layout: {
          x: measure.x,
          y: measure.y,
          width: measure.width,
          height: measure.height
        }
      })

      return
    }

    event.target?.measure((x, y, width, height, pageX, pageY) => {
      useLayoutShiftRegistryStore.getState().add({
        name,
        layout: {
          x: pageX,
          y: pageY,
          width,
          height
        },
        _eventTarget: event.target
      })
    })
  }, [])

  return {
    register,
    unregister
  }
}

type Position = 'bottom' | 'bottom-right' | 'bottom-left' | 'top' | 'top-right' | 'top-left'
type Offset = {
  top?: DimensionValue
  right?: DimensionValue
  bottom?: DimensionValue
  left?: DimensionValue
}
/**
 *
 * @param offset will override the style
 * @returns
 */
export const LayoutShiftContent = ({
  children,
  hostName,
  position = 'bottom',
  offset,
  showAnchorIndicator
}: PropsWithChildren<{ hostName: string; position?: Position; offset?: Offset; showAnchorIndicator?: boolean }>) => {
  const { registryLayout } = useLayoutShiftRegistryStore()
  const layout = registryLayout[hostName]?.layout

  type LayoutOffset = Pick<ViewStyle, 'top' | 'right' | 'left' | 'marginLeft'>
  const layoutOffset: LayoutOffset | undefined = useMemo(() => {
    if (!layout) {
      return
    }
    switch (position) {
      case 'top':
        return {
          top: Math.round(layout.y)
        }
      case 'top-left':
        return {
          top: Math.round(layout.y),
          left: Math.round(layout.x)
        }
      case 'top-right':
        return {
          top: Math.round(layout.y),
          left: Math.round(layout.x + layout.width)
        }
      case 'bottom':
        return {
          top: Math.round(layout.y + layout.height),
          left: 0,
          right: 0
        }
      case 'bottom-left':
        return {
          top: Math.round(layout.y + layout.height),
          left: Math.round(layout.x)
        }
      case 'bottom-right':
        return {
          top: Math.round(layout.y + layout.height),
          left: layout?.x + Math.round(layout.width)
        }
    }
  }, [layout, position])

  const SHOW_ANCHOR_INDICATOR = showAnchorIndicator && __DEV__ // DEV ONLY

  if (!layout) {
    console.warn(`LayoutShiftRegistry Component: missing layout with hostName "${hostName}"`)
    return null
  }

  return (
    <View
      style={[
        {
          zIndex: 99,
          position: 'absolute',
          top: layoutOffset?.top,
          left: layoutOffset?.left,
          right: layoutOffset?.right
        },
        offset
      ]}
    >
      {SHOW_ANCHOR_INDICATOR && (
        <View
          style={[
            {
              zIndex: 999,
              position: 'absolute'
            },
            offset
          ]}
        >
          <View
            style={{
              zIndex: 99,
              width: 8,
              height: 8,
              borderRadius: 4,
              backgroundColor: '#0ff',
              borderColor: '#000',
              borderWidth: 0.5
            }}
          />
        </View>
      )}
      {children}
    </View>
  )
}

export const LayoutShiftAnchor = memo(
  ({ name, children, ...props }: PropsWithChildren<{ name: string } & ComponentProps<typeof View>>) => {
    const { register, unregister } = useLayoutShiftRegistry()

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      return () => unregister(name)
    }, [])

    return (
      <View {...props} onLayout={(e) => register(name, e)} id={`layoutShiftAnchor-${name}`}>
        {children}
      </View>
    )
  }
)

export const layoutShiftProviderRef = createRef<View>()

export const LayoutShiftProvider = ({ children, ...props }: PropsWithChildren<ComponentProps<typeof View>>) => {
  const { updateLayout, registryLayout } = useLayoutShiftRegistryStore()

  const updateRegistryLayout = useCallback(
    (registry: LayoutRegistry) => {
      registry._eventTarget.measureInWindow((x, y, width, height) => {
        updateLayout(registry.name, {
          x,
          y,
          width,
          height
        })
      })
    },
    [updateLayout]
  )

  const repaintLayout = useCallback(() => {
    for (const registry in registryLayout) {
      updateRegistryLayout(registryLayout[registry])
    }
  }, [registryLayout, updateRegistryLayout])

  useEffect(() => {
    const signalController = new AbortController()
    isWeb && window?.addEventListener('resize', repaintLayout, { signal: signalController.signal })

    return () => {
      signalController.abort()
    }
  }, [repaintLayout])

  return (
    <View ref={layoutShiftProviderRef} {...props}>
      <PortalHost name="layoutShiftProvider" />
      {children}
    </View>
  )
}
